import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import {
  lowestConferringFixedLevel,
  MAX_GRANT_CHANGES_PER_BATCH,
  onlyPermissionOf,
  type PermissionSetEntity,
} from '@omnea-digital/authz-model'
import { AssertNeverError } from '@omnea-digital/toolbox'
import { Fragment, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InitialsAvatar } from '@omnea-app/fe-users-and-groups/avatar/InitialsAvatar'
import { DialogHeader } from '@omnea-app/omui/DialogHeader'
import { PERMISSION_LABEL_KEYS } from '../permissionLabels'
import { usePermissionSets } from '../usePermissionSets'
import { ACCESS_DIALOG_BY_ENTITY, TAB_BY_KIND, type AccessTab } from './accessDialogByEntity'
import { AddRecordAccessSubject } from './AddRecordAccessSubject'
import { InheritedPermissionsSummary } from './InheritedPermissionsSummary'
import { RecordAccessLevelMenu } from './RecordAccessLevelMenu'
import {
  grantConfigurationOf,
  grantSubjectOf,
  hasDuplicateSubject,
  isArchivedSubject,
  recordGrantDiff,
  subjectKey,
  type DraftGrant,
} from './recordGrantDiff'
import { grantConfers } from './recordGrantLevels'
import { useRecordGrants, type RecordAccessHolder } from './useRecordGrants'
import {
  useUpdateRecordAccessGrant,
  type RecordAccessGrantChange,
  type RecordGrantConfiguration,
  type RecordGrantSubject,
} from './useUpdateRecordAccessGrant'
import type { TFunction } from 'i18next'

type Props = {
  entity: PermissionSetEntity
  recordId: string
  recordName?: string
  open: boolean
  onClose: () => void
}

const subjectIdOf = (subject: RecordGrantSubject): string => {
  switch (subject.kind) {
    case 'user':
      return subject.userId
    case 'role':
      return subject.roleId
    case 'group':
      return subject.groupId
    case 'user_property':
      return subject.valueId
  }
}

const holderName = (holder: RecordAccessHolder): string => {
  switch (holder.subject.kind) {
    case 'user':
      return `${holder.subject.user.firstName} ${holder.subject.user.lastName}`
    case 'role':
      return holder.subject.role.name
    case 'group':
      return holder.subject.group.name
    case 'user_property':
      return holder.subject.value.name
  }
}

const isEditableKind = (entity: PermissionSetEntity, subject: RecordGrantSubject): boolean =>
  ACCESS_DIALOG_BY_ENTITY[entity].tabs.includes(TAB_BY_KIND[subject.kind])

const isOnTab = (subject: RecordGrantSubject, tab: AccessTab): boolean => TAB_BY_KIND[subject.kind] === tab

const NOTE_SEPARATOR = ' · '

const kindNote = (subject: RecordGrantSubject, t: TFunction<'settings', 'record-access'>): string | null => {
  switch (subject.kind) {
    case 'group':
      return t('group-subject')
    case 'user_property':
      return t(`user-attribute.${subject.dimension}`)
    case 'user':
    case 'role':
      return null
    default:
      throw new AssertNeverError(subject)
  }
}

const archivedNote = (subject: RecordGrantSubject, t: TFunction<'settings', 'record-access'>): string => {
  switch (subject.kind) {
    case 'user_property':
      return t('archived-user-attribute')
    case 'user':
    case 'group':
    case 'role':
      return t('archived-subject')
    default:
      throw new AssertNeverError(subject)
  }
}

// Null rather than an empty element when no note applies: any secondary at all gives the row a second line.
const noteLine = (notes: readonly ReactNode[]): ReactNode => {
  const shownNotes = notes.filter((note) => note != null)
  if (shownNotes.length === 0) return null
  return shownNotes.map((note, i) => (
    <Fragment key={i}>
      {i > 0 && NOTE_SEPARATOR}
      {note}
    </Fragment>
  ))
}

const draftsOf = (entity: PermissionSetEntity, holders: readonly RecordAccessHolder[]): DraftGrant[] =>
  holders.flatMap((holder) => {
    const subject = grantSubjectOf(holder)
    const configuration = grantConfigurationOf(holder.directGrant)
    if (configuration == null || !isEditableKind(entity, subject)) return []
    return [
      {
        subject,
        configuration,
        canOnlyBeRemoved: isArchivedSubject(holder),
      },
    ]
  })

const DIALOG_HEIGHT = 560

const grantsOnTab = (grants: readonly DraftGrant[], tab: AccessTab): readonly DraftGrant[] =>
  grants.filter((grant) => isOnTab(grant.subject, tab))

type AccessListPaneProps = {
  heading?: ReactNode
  /** Absent where the section is only rendered when it has rows. */
  emptyText?: string
  children: ReactNode
}

const AccessListPane = ({ heading, emptyText, children }: AccessListPaneProps) => (
  <Stack gap={1} sx={{ flexShrink: 0 }}>
    {heading}
    <List disablePadding>{children}</List>
    {emptyText != null && (
      <Typography variant="body-regular" component="p" color="text.muted">
        {emptyText}
      </Typography>
    )}
  </Stack>
)

type EditorProps = {
  entity: PermissionSetEntity
  recordId: string
  holders: readonly RecordAccessHolder[]
  tab: AccessTab
  onTabChange: (tab: AccessTab) => void
  isSaving: boolean
  saveFailed: boolean
  /** A refetch failed under the open editor, so these rows are the last ones that loaded. */
  reloadFailed: boolean
  onClose: () => void
  onSave: (changes: readonly RecordAccessGrantChange[]) => Promise<void>
}

const RecordAccessEditor = ({
  entity,
  recordId,
  holders,
  tab,
  onTabChange,
  isSaving,
  saveFailed,
  reloadFailed,
  onClose,
  onSave,
}: EditorProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const { t: permissionSetsT } = useTranslation('settings', { keyPrefix: 'permission-sets' })
  const { data: setsData } = usePermissionSets()
  const { tabs, recordControl: RecordControl } = ACCESS_DIALOG_BY_ENTITY[entity]
  const onlyPermission = onlyPermissionOf(entity)
  const [held, setHeld] = useState<readonly DraftGrant[]>(() => draftsOf(entity, holders))
  const [pending, setPending] = useState<readonly DraftGrant[]>(held)
  // A subject picked here is in no server response yet, so its name has no other source.
  const [pickedNames, setPickedNames] = useState<ReadonlyMap<string, string>>(new Map())

  const entityName = t(`entity.${entity}`)
  const entitySets = (setsData?.permissionSets ?? []).filter((set) => set.entity === entity)
  const setPermissionsById = new Map<string, readonly string[]>(entitySets.map((set) => [set.id, set.permissions]))
  // A set the catalogue no longer offers — archived, or not yet loaded — still names itself on the
  // grant that holds it, so a held row reads correctly before and without the catalogue.
  const heldSets = holders.flatMap((holder) =>
    holder.directGrant.kind === 'custom' ? [{ id: holder.directGrant.permissionSetId, name: holder.directGrant.name }] : []
  )
  const nameById = new Map(holders.map((holder) => [subjectKey(grantSubjectOf(holder)), holderName(holder)]))
  // Read afresh each render rather than carried on the draft, so a refetch that ends someone's
  // participation stops promising their access survives a revoke.
  const participatingSubjects = new Set(
    holders.flatMap((holder) => (holder.derivedPermissions.length > 0 ? [subjectKey(grantSubjectOf(holder))] : []))
  )
  const pendingSubjects = new Set(pending.map((grant) => subjectKey(grant.subject)))
  // This pane reads the last response and the granted one reads the draft, so a subject present in
  // both must be excluded from exactly one or they read as doubled, or as gone.
  const inheritedOnlyHolders = holders.filter(
    (holder) =>
      isOnTab(grantSubjectOf(holder), tab) &&
      participatingSubjects.has(subjectKey(grantSubjectOf(holder))) &&
      !pendingSubjects.has(subjectKey(grantSubjectOf(holder)))
  )

  const tabPending = grantsOnTab(pending, tab)
  const tabChanges = recordGrantDiff({ held: grantsOnTab(held, tab), pending: tabPending })
  const hasRepeatedSubject = hasDuplicateSubject(tabPending)
  const exceedsChangeLimit = tabChanges.length > MAX_GRANT_CHANGES_PER_BATCH
  const hasUnsavedChanges = tabChanges.length > 0

  // Only the saved tab becomes the new baseline, so a later save on this tab cannot re-send what
  // landed, and a tab this batch never carried keeps the baseline its own edit began from.
  const saveTab = async () => {
    const savedTab = tab
    await onSave(tabChanges).then(
      () => setHeld([...held.filter((grant) => !isOnTab(grant.subject, savedTab)), ...grantsOnTab(pending, savedTab)]),
      () => undefined
    )
  }

  const discardTab = () => setPending([...pending.filter((grant) => !isOnTab(grant.subject, tab)), ...grantsOnTab(held, tab)])

  const nameOf = (subject: RecordGrantSubject): string =>
    nameById.get(subjectKey(subject)) ?? pickedNames.get(subjectKey(subject)) ?? t('unknown-subject')

  const add = (subject: RecordGrantSubject, name: string) => {
    if (pending.some((grant) => subjectKey(grant.subject) === subjectKey(subject))) return
    setPending([
      ...pending,
      { subject, configuration: { kind: 'fixed', level: lowestConferringFixedLevel(entity) }, canOnlyBeRemoved: false },
    ])
    setPickedNames(new Map(pickedNames).set(subjectKey(subject), name))
  }

  const remove = (subject: RecordGrantSubject) =>
    setPending(pending.filter((grant) => subjectKey(grant.subject) !== subjectKey(subject)))

  const reconfigure = (subject: RecordGrantSubject, configuration: RecordGrantConfiguration) =>
    setPending(pending.map((grant) => (subjectKey(grant.subject) === subjectKey(subject) ? { ...grant, configuration } : grant)))

  const tabPendingIds = tabPending.map((grant) => subjectIdOf(grant.subject))

  const grants = (
    <>
      {tabs.length > 1 && (
        <Stack gap={1} sx={{ flexShrink: 0 }}>
          <Tabs
            shape="rounded"
            value={tab}
            onChange={(_, selectedTab: AccessTab) => onTabChange(selectedTab)}
            aria-label={t('tabs-aria')}
          >
            {tabs.map((offeredTab) => (
              <Tab
                key={offeredTab}
                value={offeredTab}
                label={t(`tab.${offeredTab}`)}
                disabled={hasUnsavedChanges && offeredTab !== tab}
              />
            ))}
          </Tabs>
          {hasUnsavedChanges && (
            <Typography variant="small-regular" component="p" color="text.muted">
              {t('tab-locked-hint')}
            </Typography>
          )}
        </Stack>
      )}

      <AddRecordAccessSubject tab={tab} hiddenSubjectIds={tabPendingIds} disabled={isSaving} onAdd={add} />
      <Stack gap={3} sx={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
        <AccessListPane emptyText={tabPending.length === 0 ? t(`granted-empty.${tab}`, { entity: entityName }) : undefined}>
          {tabPending.map((grant) => (
            <ListItem
              key={subjectKey(grant.subject)}
              disableGutters
              secondaryAction={
                <RecordAccessLevelMenu
                  level={
                    onlyPermission != null && grantConfers(entity, grant.configuration, onlyPermission, setPermissionsById)
                      ? { kind: 'label-only', label: permissionSetsT(PERMISSION_LABEL_KEYS[onlyPermission]) }
                      : {
                          kind: 'configurable',
                          configuration: grant.configuration,
                          permissionSets: entitySets,
                          heldSets,
                          canOnlyBeRemoved: grant.canOnlyBeRemoved,
                          onChange: (configuration) => reconfigure(grant.subject, configuration),
                        }
                  }
                  disabled={isSaving}
                  subjectName={nameOf(grant.subject)}
                  onRemove={() => remove(grant.subject)}
                />
              }
            >
              <ListItemAvatar>
                <InitialsAvatar fullName={nameOf(grant.subject)} colourSubjectId={subjectIdOf(grant.subject)} size={32} />
              </ListItemAvatar>
              <ListItemText
                primary={nameOf(grant.subject)}
                secondary={noteLine([
                  kindNote(grant.subject, t),
                  grant.canOnlyBeRemoved ? archivedNote(grant.subject, t) : null,
                  // Load-bearing: it is what tells an admin that removing this grant leaves the
                  // subject still able to see the record.
                  participatingSubjects.has(subjectKey(grant.subject)) ? t('inherited-access-survives-removal') : null,
                ])}
                slotProps={{ primary: { noWrap: true } }}
              />
            </ListItem>
          ))}
        </AccessListPane>

        {inheritedOnlyHolders.length > 0 && (
          <AccessListPane
            heading={
              <Stack gap={0.5}>
                <Typography variant="small-medium" component="p">
                  {t('inherited-heading')}
                </Typography>
                <Typography variant="small-regular" component="p" color="text.muted">
                  {t('inherited-note')}
                </Typography>
              </Stack>
            }
          >
            {inheritedOnlyHolders.map((holder) => (
              <ListItem key={subjectKey(grantSubjectOf(holder))} disableGutters>
                <ListItemAvatar>
                  <InitialsAvatar fullName={holderName(holder)} colourSubjectId={subjectIdOf(grantSubjectOf(holder))} size={32} />
                </ListItemAvatar>
                <ListItemText
                  primary={holderName(holder)}
                  secondary={noteLine([
                    kindNote(grantSubjectOf(holder), t),
                    <InheritedPermissionsSummary key="inherited-permissions" permissions={holder.derivedPermissions} />,
                  ])}
                  slotProps={{ primary: { noWrap: true }, secondary: { component: 'div' } }}
                />
              </ListItem>
            ))}
          </AccessListPane>
        )}
      </Stack>
      <Stack gap={2} sx={{ flexShrink: 0 }}>
        {hasRepeatedSubject && <Alert severity="error">{t('duplicate-subject-error')}</Alert>}
        {exceedsChangeLimit && <Alert severity="error">{t('over-cap-error', { max: MAX_GRANT_CHANGES_PER_BATCH })}</Alert>}
        {saveFailed && <Alert severity="error">{t('save-error')}</Alert>}
        {reloadFailed && <Alert severity="warning">{t('reload-error')}</Alert>}
      </Stack>
    </>
  )

  return (
    <>
      <DialogContent>
        <Stack gap={3} sx={{ minHeight: 0, flex: 1 }}>
          <Typography variant="body-regular" component="p" color="text.muted">
            {t(tabs.includes('user-attributes') ? 'description-with-user-attributes' : 'description', {
              entity: entityName,
            })}
          </Typography>
          {RecordControl == null ? (
            grants
          ) : (
            <RecordControl recordId={recordId} locked={hasUnsavedChanges} grants={grants} savedGrants={held} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('close')}
        </Button>
        <Button onClick={discardTab} disabled={!hasUnsavedChanges || isSaving}>
          {t('discard')}
        </Button>
        <Button
          variant="contained"
          disabled={!hasUnsavedChanges || hasRepeatedSubject || exceedsChangeLimit || isSaving}
          onClick={() => void saveTab()}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </>
  )
}

const OpenRecordAccessDialog = ({ entity, recordId, recordName, onClose }: Omit<Props, 'open'>) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const { data, isError, isFetchedAfterMount } = useRecordGrants(entity, recordId)
  const [tab, setTab] = useState<AccessTab>('users-and-groups')
  const {
    mutateAsync: applyChanges,
    isPending: isSaving,
    isError: saveFailed,
    reset,
  } = useUpdateRecordAccessGrant(entity, recordId)

  const close = () => {
    if (isSaving) return
    reset()
    onClose()
  }

  const save = async (changes: readonly RecordAccessGrantChange[]) => {
    await applyChanges([...changes])
  }

  return (
    <Dialog
      open
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: DIALOG_HEIGHT, maxHeight: '90vh' } }}
      // The actions menu that opens this dialog stays mounted around it, and its MenuList takes a
      // bare letter as type-ahead — so a name typed into a picker would jump focus to a menu item.
      onKeyDown={(event) => event.stopPropagation()}
    >
      <DialogHeader
        title={recordName == null ? t('title-unnamed') : t('title', { record: recordName })}
        onClose={close}
        disabled={isSaving}
      />
      {data == null && !isError && (
        <DialogContent>
          <Skeleton variant="rounded" height={160} />
        </DialogContent>
      )}
      {/* Only when the read left nothing to edit: a refetch failing under a loaded editor would
          otherwise replace it, discarding whatever the admin has staged. */}
      {isError && data == null && (
        <DialogContent>
          <Alert severity="error">{t('load-error')}</Alert>
        </DialogContent>
      )}
      {isFetchedAfterMount && data != null && (
        <RecordAccessEditor
          entity={entity}
          recordId={recordId}
          holders={data.access.holders}
          tab={tab}
          onTabChange={setTab}
          isSaving={isSaving}
          saveFailed={saveFailed}
          reloadFailed={isError}
          onClose={close}
          onSave={save}
        />
      )}
    </Dialog>
  )
}

/**
 * The menus that host this dialog render it on every qualifying page load, so nothing is rendered
 * until it opens: each opening reads the record's access afresh, rather than editing a list cached by
 * an earlier one while another admin has moved on.
 */
export const ManageRecordAccessDialog = ({ open, ...props }: Props) => (open ? <OpenRecordAccessDialog {...props} /> : null)
